<template>
  <form class="details-content__form content-form" @submit.prevent="submit">
    <component
      :is="fieldComponent"
      v-for="(fieldComponent, fieldName) in fieldComponents"
      :key="fieldName"
      v-model="formAddress[fieldName]"
      :error="errors ? errors[fieldName] : null"
      :validation-rules="validationRules[fieldName]"
      v-bind="fieldName === 'closing_date' ? { from: formAddress.opening_date } : {}"
      @focusout="validate(fieldName)"
    />
    <span class="content-form__required">{{ trans('addresses.details.form.required') }}</span>
    <button
      class="content-form__submit btn btn--primary"
      type="submit"
      :class="{ 'loading-btn': loading }"
    >
      {{ trans('addresses.details.form.submit') }}
    </button>
  </form>
</template>

<script>
import { translationMixin } from '@elements/translation';
import { useAddressStore } from '@stores/useAddressStore';
import { flashBagError, flashBagSuccess } from '@tools/flashbag';
import { momentMixin } from '@tools/moment';
import { requestsMixin } from '@tools/requests-mixin';
import { URL_XHR_ADDRESS_SERVICES_UPDATE } from '@tools/routes/routes';
import { camelCase, isString, keys, mapKeys, pick, snakeCase, some } from 'lodash-es';
import { markRaw } from 'vue';
import AddressDetailsFormMeetingRooms from './AddressDetailsFormMeetingRooms.vue';
import AddressDetailsFormPhoneBoxes from './AddressDetailsFormPhoneBoxes.vue';

export default {
  mixins: [ translationMixin, momentMixin, requestsMixin ],
  props: {
    address: { type: Object, required: true },
  },
  setup() {
    const store = useAddressStore();
    return { store };
  },
  data() {
    const initErrors = {
      meeting_rooms: null,
      phone_boxes: null,
    };
    return {
      formAddress: {
        ...this.address,
      },
      loading: false,
      saved: false,
      fieldComponents: {
        meeting_rooms: markRaw(AddressDetailsFormMeetingRooms),
        phone_boxes: markRaw(AddressDetailsFormPhoneBoxes),
      },
      validationRules: {
        meeting_rooms: { min: 0, max: 250 },
        phone_boxes: { min: 0, max: 250 },
      },
      initErrors,
      errors: { ...initErrors },
    };
  },
  computed: {
    formBody() {
      return mapKeys(pick(this.formAddress, keys(this.fieldComponents)), (val, key) => camelCase(key));
    },
    valid() {
      return !some(this.errors, isString);
    },
  },
  mounted() {
    window.onbeforeunload = () => {
      this.validate();
      return this.valid ? null : this.trans('addresses.details.form.validation.unload_alert');
    };
  },
  methods: {
    validate(fieldToValidate) {
      const fields = (fieldToValidate === undefined) ? keys(this.fieldComponents) : [ fieldToValidate ];
      fields.forEach((field) => {
        this.errors[field] = null;
        const rules = this.validationRules[field] || {};
        const value = this.formAddress[field];

        // Check if the field is required or not
        const isRequired = rules.required === true;

        if (value === null || value === '') {
          if (isRequired) {
            this.errors[field] = this.trans(`addresses.details.form.validation.${field}.required`);
          }
          this.updateStore();
          // If field is not required and is empty, no error should be set
        } else {
          keys(rules).forEach((ruleKey) => {
            let valid;
            switch (ruleKey) {
              case 'min':
                valid = value >= rules[ruleKey];
                break;
              case 'max':
                valid = value <= rules[ruleKey];
                break;
              default:
                valid = true; // Assume other validations pass if not specified
            }

            if (valid) {
              this.updateStore();
              return;
            }

            this.errors[field] = this.trans(`addresses.details.form.validation.${field}.${ruleKey}`);
          });
        }
      });
      window.onbeforeunload = () => ((this.saved && this.valid) ? null : this.trans('addresses.details.form.validation.unload_alert'));
    },
    async submit() {
      if (this.loading) {
        return false;
      }
      this.validate();
      if (!this.valid) {
        return false;
      }
      this.saved = false;
      this.errors = { ...this.initErrors };
      this.loading = true;
      try {
        await this.put(URL_XHR_ADDRESS_SERVICES_UPDATE.replace('{id}', this.address.id), this.formBody, { emulateJSON: false });
        flashBagSuccess(this.trans('addresses.details.form.success'));
        this.saved = true;
      } catch (err) {
        this.errors = { ...this.errors, ...mapKeys(err.response.data, (val, key) => snakeCase(key)) };
        flashBagError(this.trans('addresses.details.form.error'));
      }
      this.loading = false;
      return true;
    },
    updateStore() {
      this.store.setAddress(this.formAddress);
    },
  },
};
</script>
